/* eslint-disable @next/next/no-img-element */
'use client'
import { Icon } from '@/components'
import { useListReview, useReviewsSumary } from '@/features/products'
import { IListReview } from '@/features/products/types/review'
import { MAX_ITEMS_REVIEW } from '@/features/products/utils/constant'
import { formatNumber, getOptimizedImageUrl } from '@/utils/helpers'
import { Fancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type Props = {
  data?: Product
}

const MIN_IMAGES_TO_SHOW = 3 // Số ảnh tối thiểu để hiển thị widget

const handleGenListImagesForFancybox = (images: string[]) => {
  return images?.map((image, index) => ({
    src: getOptimizedImageUrl(image, 1920, 90), // Mobile: 1200px width, quality 90
    thumb: getOptimizedImageUrl(image, 1200, 90), // Thumbnail nhỏ hơn
    preload: index < 2,
  }))
}
export const SwiperImagesFeedback = ({ data }: Props) => {
  const defaultImages = data?.media_gallery?.map((item) => item?.url).slice(4) || []
  const t = useTranslations()
  const [show, setShow] = useState(true)
  const [params, setParams] = useState<IOptionsProductProps>({ currentPage: 1, pageSize: MAX_ITEMS_REVIEW })
  const [allImages, setAllImages] = useState<string[]>(defaultImages)
  const isLoadingMoreRef = useRef(false)
  const hasMorePagesRef = useRef(true)
  const isInitialLoadRef = useRef(true)
  const fancyboxInstanceRef = useRef<any>(null)
  const carouselRef = useRef<any>(null)

  const { data: reviewsData, isLoading: isLoadingReviews } = useListReview({
    productId: data?.id,
    options: {},
    params: params,
  })
  const { reviewSummary } = useReviewsSumary(data?.id || 0)

  const totalRating = reviewSummary?.avg_rating || 0
  const totalReviews = reviewSummary?.total_review || 0

  // Memoize hàm xử lý ảnh từ reviewsData.items - API mới trả về media array
  const getListImages = useCallback((data: IListReview | null | undefined) => {
    if (!data || !data?.items || data.items.length === 0) {
      return {
        all: [],
        unique: [],
        firstThreeImages: [],
      }
    }
    const images: string[] = []

    for (const review of data.items) {
      if (review?.media && Array.isArray(review.media)) {
        for (const mediaItem of review.media) {
          // Chỉ lấy media type là 'image' và có URL
          if (mediaItem && mediaItem.type === 'image' && mediaItem.url) {
            images.push(mediaItem.url)
          }
        }
      }
    }

    const uniqueImages = Array.from(new Set(images))
    return {
      all: images,
      unique: uniqueImages,
      firstThreeImages: uniqueImages?.slice(0, 3) || [],
    }
  }, [])

  const { firstThreeImages } = useMemo(() => getListImages(reviewsData), [reviewsData, getListImages])

  // Load thêm ảnh
  const loadMoreImages = useCallback(() => {
    if (isLoadingMoreRef.current || !hasMorePagesRef.current) return

    isLoadingMoreRef.current = true

    setParams((prev) => {
      const currentPage = prev?.currentPage ?? 1
      const nextPage = currentPage + 1
      return { ...prev, currentPage: nextPage }
    })

    setTimeout(() => {
      isLoadingMoreRef.current = false
    }, 1000)
  }, [])

  // Cập nhật allImages khi có dữ liệu mới
  useEffect(() => {
    if (reviewsData) {
      const { unique: newUniqueImages } = getListImages(reviewsData)

      setAllImages((prev) => {
        const combined = [...prev, ...newUniqueImages]
        const uniqueCombined = Array.from(new Set(combined))
        return uniqueCombined
      })

      if (carouselRef.current && newUniqueImages.length > 0) {
        carouselRef.current.add(handleGenListImagesForFancybox(newUniqueImages))
      }

      hasMorePagesRef.current = reviewsData?.page_info?.current_page < reviewsData?.page_info?.total_pages
    }
  }, [reviewsData, getListImages])

  // Auto-load thêm trang nếu chưa đủ ảnh
  useEffect(() => {
    if (isInitialLoadRef.current && reviewsData && !isLoadingReviews) {
      const currentImagesCount = allImages.length

      if (currentImagesCount < MIN_IMAGES_TO_SHOW && hasMorePagesRef.current) {
        loadMoreImages()
      } else if (currentImagesCount >= MIN_IMAGES_TO_SHOW || !hasMorePagesRef.current) {
        isInitialLoadRef.current = false
      }
    }
  }, [reviewsData, allImages.length, isLoadingReviews, loadMoreImages])

  // Xử lý khi mở gallery với Fancybox
  const handleOpenGallery = useCallback(() => {
    const fancyboxImages = handleGenListImagesForFancybox(allImages)

    fancyboxInstanceRef.current = Fancybox.show(fancyboxImages, {
      infinite: true,
      mainClass: 'fancybox-custom',
      on: {
        // Xử lý lazy load khi slide gần cuối
        'Carousel.change': (fancybox: any, carousel: any) => {
          const currentIndex = carousel?.getPageIndex()
          const totalSlides = carousel?.getSlides()?.length
          const isNearEnd = currentIndex >= totalSlides - 5
          carouselRef.current = carousel
          if (isNearEnd && hasMorePagesRef.current && !isLoadingMoreRef.current) {
            loadMoreImages()
          }
        },
        // Cleanup khi đóng
        close: () => {
          fancyboxInstanceRef.current = null
        },
      },
    } as any)
  }, [allImages, loadMoreImages])

  // // Cập nhật Fancybox khi có thêm ảnh mới
  // useEffect(() => {
  //   if (fancyboxInstanceRef.current && allImages.length > 0) {
  //     // Fancybox tự động cập nhật carousel khi có ảnh mới
  //   }
  // }, [allImages])

  const handleHide = () => {
    setShow(false)
  }

  // Chỉ hiển thị nếu có đủ ảnh
  const isShow = show && allImages.length >= MIN_IMAGES_TO_SHOW && reviewsData?.total_count !== 0
  if (isLoadingReviews && allImages.length === 0) {
    return null
  }

  return (
    <>
      <AnimatePresence>
        {isShow && !!reviewsData?.items?.length && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2,
            }}
          >
            <Stack
              sx={{
                padding: 1,
                position: 'relative',
                backgroundColor: 'primary.contrastText',
                color: 'primary.main',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                borderRadius: '8px',
                maxWidth: '118px',
              }}
              gap={1}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography fontWeight={600} fontSize="10px" sx={{ lineHeight: 1.3 }}>
                  {formatNumber(totalReviews)} {t('lượt đánh giá')}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent={'center'}
                  sx={{
                    color: 'primary.contrastText',
                    p: '5px 4px',
                    backgroundColor: 'primary.main',
                    borderRadius: '4px',
                    fontSize: 'inherit',
                    width: 'fit-content',
                  }}
                >
                  <Typography fontWeight={600} fontSize="inherit" lineHeight={1}>
                    {totalRating.toFixed(1)}
                  </Typography>
                  <Icon.Star fontSize="inherit" sx={{ color: '#FEB50D' }} />
                </Stack>
              </Stack>

              <Box
                position={'relative'}
                mt={1}
                sx={{ cursor: 'pointer', width: '102px', height: '76px' }}
                onClick={handleOpenGallery}
              >
                {firstThreeImages.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: index === 0 ? 'relative' : 'absolute',
                      transform: `scale(${1 - index * 0.1})`,
                      top: index === 0 ? 0 : `${-index * 8}px`,
                      left: 0,
                      borderRadius: '4px',
                      overflow: 'hidden',
                      zIndex: firstThreeImages.length - index,
                      display: 'inline-block',
                    }}
                  >
                    <Image
                      style={{ width: '100%', height: 'auto' }}
                      src={image}
                      quality={90}
                      loading="lazy"
                      sizes="100vw"
                      alt={`Review image ${index + 1}`}
                      width={100}
                      height={100}
                    />
                  </Box>
                ))}
              </Box>

              <IconButton
                onClick={handleHide}
                aria-label="close"
                sx={{
                  padding: '0px',
                  backgroundColor: 'five.main',
                  color: 'white',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  transform: 'translate(50%, -50%)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'five.main',
                    opacity: 0.8,
                    scale: 1.05,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: '20px', padding: '4px' }} />
              </IconButton>
            </Stack>
          </Box>
        )}
      </AnimatePresence>
    </>
  )
}
